import { Injectable, OnDestroy, effect } from '@angular/core';
import { Subject, firstValueFrom } from 'rxjs';
import { TranslocoService } from '@jsverse/transloco';
import { type HeaderConfig, type RolePlayAttempt, type CtaConfig, type FooterSectionConfig } from '@qai/ui';
import { IPracticeCallAttemptResult, IQuizAttemptResult } from 'src/app/interfaces/ipathway-enrollment';
import { PathwayStepState, PathwayStepType, isRoleplayStepType } from 'src/app/utils/enums/learner/pathway.enum';
import { LocaleService } from 'src/app/services/locale.service';
import { PathwayRoleplayResultsService } from 'src/app/pages/learner/results/results-pathway-roleplay/service/pathway-roleplay-results.service';
import { PathwaysService } from 'src/app/services/learner/pathways.service';
import { AppConstants } from 'src/app/app-constants';
import { PathwayLocalStorageUtil } from 'src/app/utils/pathway-localstorage.util';
import type { PathwayStep } from '../pathway-details.component';

@Injectable()
export class PathwayStepPollingService implements OnDestroy {
  readonly shouldRefreshEnrollment$ = new Subject<void>();
  lastReadyPracticeCallUuid: string | null = null;
  lastReadyQuizPracticeCallUuid: string | null = null;

  private readonly resultToBeViewedStorageKey = 'pathway_practice_call_results_to_be_viewed';
  private resultToBeViewedUuids = new Set<string>();
  private pendingEnrollmentRefreshUuids = new Set<string>();
  private awaitingEnrollmentRefresh = false;
  private currentPollingUuid: string | null = null;

  private quizPollingInterval: ReturnType<typeof setInterval> | null = null;
  private quizPollingStartTime = 0;
  private isQuizPolling = false;

  constructor(
    private readonly translocoService: TranslocoService,
    private readonly localeService: LocaleService,
    private readonly resultsService: PathwayRoleplayResultsService,
    private readonly pathwaysService: PathwaysService,
  ) {
    effect(() => {
      if (this.resultsService.resultReady()) {
        if (this.currentPollingUuid) {
          this.lastReadyPracticeCallUuid = this.currentPollingUuid;
          this.resultToBeViewedUuids.add(this.currentPollingUuid);
          this.persistResultToBeViewedToStorage();
          this.pendingEnrollmentRefreshUuids.add(this.currentPollingUuid);
        }
        PathwayLocalStorageUtil.clearRoleplayPending();
        this.currentPollingUuid = null;
        this.awaitingEnrollmentRefresh = true;
        setTimeout(() => this.shouldRefreshEnrollment$.next(), 1000);
      }
    });

    effect(() => {
      if (this.resultsService.isTimedOut() && this.currentPollingUuid) {
        PathwayLocalStorageUtil.clearRoleplayPending();
        this.currentPollingUuid = null;
      }
    });
  }

  checkAndStartPolling(steps: PathwayStep[], moduleAssignmentUuid: string): void {
    this.restoreUuidsFromStorage(this.resultToBeViewedStorageKey, this.resultToBeViewedUuids, steps);
    if (this.awaitingEnrollmentRefresh) {
      this.pendingEnrollmentRefreshUuids.clear();
      this.awaitingEnrollmentRefresh = false;
    }
    this.startPollingIfProcessing(steps);
    this.startQuizPollingIfPending(steps, moduleAssignmentUuid);
  }

  stopPolling(): void {
    this.resultsService.destroy();
    this.currentPollingUuid = null;
    this.stopQuizPolling();
  }

  private startQuizPollingIfPending(steps: PathwayStep[], moduleAssignmentUuid: string): void {
    if (this.isQuizPolling) return;
    const pendingStep = this.getPendingQuizStep(steps);
    if (!pendingStep) return;

    this.isQuizPolling = true;
    this.quizPollingStartTime = Date.now();

    const poll = async (): Promise<void> => {
      if (!this.isQuizPolling) return;
      try {
        const enrollment = await firstValueFrom(this.pathwaysService.getorCreatePathwayEnrollment(moduleAssignmentUuid));
        const allSteps = enrollment.focusAreaGroups.flatMap(g => g.steps);
        const quizStep = allSteps.find(s =>
          s.type === PathwayStepType.QUIZ &&
          (s.results?.length ?? 0) > 0 &&
          (s.results![0] as any).overAllScore !== null
        );
        if (quizStep) {
          this.stopQuizPolling();
          this.lastReadyQuizPracticeCallUuid = (quizStep.results![0] as any).practiceCallUuid ?? null;
          setTimeout(() => this.shouldRefreshEnrollment$.next(), 1000);
          return;
        }
      } catch { }

      const elapsed = Date.now() - this.quizPollingStartTime;
      if (elapsed >= AppConstants.RESULTS_POLLING_DURATION_MS) {
        this.stopQuizPolling();
      }
    };

    poll();
    this.quizPollingInterval = setInterval(() => poll(), AppConstants.RESULTS_POLLING_INTERVAL_MS);
  }

  isQuizAttemptProcessing(result: IQuizAttemptResult): boolean {
    return PathwayLocalStorageUtil.getQuizPendingUuid() === result.practiceCallUuid;
  }

  private getPendingQuizStep(steps: PathwayStep[]): PathwayStep | undefined {
    return steps.find(s =>
      s.stepType === PathwayStepType.QUIZ &&
      (s.quizAttempts?.length ?? 0) > 0 &&
      this.isQuizAttemptProcessing(s.quizAttempts![0])
    );
  }

  private stopQuizPolling(): void {
    this.isQuizPolling = false;
    PathwayLocalStorageUtil.clearQuizPending();
    if (this.quizPollingInterval) {
      clearInterval(this.quizPollingInterval);
      this.quizPollingInterval = null;
    }
  }

  isAttemptProcessing(result: IPracticeCallAttemptResult): boolean {
    return PathwayLocalStorageUtil.getRoleplayPendingUuid() === result.practiceCallUuid;
  }

  attemptHasResults(result: IPracticeCallAttemptResult): boolean {
    return !!result.analysisItems?.length;
  }

  getMostRecentAttempt(attempts: IPracticeCallAttemptResult[]): IPracticeCallAttemptResult | null {
    return attempts?.[0] ?? null;
  }

  getRolePlayStepAttempts(step: PathwayStep): RolePlayAttempt[] {
    if (!step.practiceCallAttempts?.length) return [];
    return this.mapRolePlayStepAttempts(step.practiceCallAttempts);
  }

  mapRolePlayStepAttempts(results: IPracticeCallAttemptResult[]): RolePlayAttempt[] {
    if (!results?.length) return [];
    const scope = 'learningPathway';
    const mostRecentAttempt = this.getMostRecentAttempt(results);
    const incompleteLabel = this.translocoService.translate('INCOMPLETE_CALL', {}, 'assignment');

    return results.map((r) => {
      const hasNoResults = !this.attemptHasResults(r);
      const isProcessing = this.isAttemptProcessing(r) || this.pendingEnrollmentRefreshUuids.has(r.practiceCallUuid);
      const isMostRecent = mostRecentAttempt?.practiceCallUuid === r.practiceCallUuid;
      const nullRow = { id: r.practiceCallUuid, date: this.localeService.formatTableDateTime(r.attemptDate), overallScore: null, messagingScore: null, methodologyScore: null, skillsScore: null };

      if (isMostRecent && isProcessing) {
        return { ...nullRow, results: { label: this.translocoService.translate('RESULTS_PROCESSING', {}, scope), kind: 'default' as const, size: 'xsmall', weight: 'semi-bold', clickable: false } };
      }

      // An unscored attempt was exited, abandoned or had too few interactions to score.
      if (hasNoResults && !isProcessing && !this.resultToBeViewedUuids.has(r.practiceCallUuid)) {
        return { ...nullRow, results: { label: incompleteLabel, kind: 'default' as const, size: 'xsmall', weight: 'semi-bold', clickable: false } };
      }

      const getScore = (type: string) => {
        const score = r.analysisItems?.find((i) => i.promptType === type)?.promptScore ?? null;
        if (score === null) return null;
        return Math.round(score * 10) / 10;
      };
      return {
        id: r.practiceCallUuid,
        date: this.localeService.formatTableDateTime(r.attemptDate),
        overallScore: getScore('SCORE_OVERALL'),
        messagingScore: getScore('MESSAGE_ADHERENCE') ?? getScore('MESSAGE_ADHERENCE_V2'),
        methodologyScore: getScore('SCORE_CUSTOMIZED'),
        skillsScore: getScore('SCORE_SKILLS'),
        ...(this.shouldUseHeaderViewResultsForAttempt(r, isMostRecent)
          ? { results: { label: ' ', kind: 'default' as const, clickable: false} }
          : {}),
      };
    });
  }

  getRolePlayStepColumnHeaders(step: PathwayStep): HeaderConfig[] {
    const scope = 'learningPathway';
    const completedAttempts = (step.practiceCallAttempts ?? []).filter((a) => this.attemptHasResults(a));
    const hasScoreType = (promptType: string): boolean =>
      completedAttempts.length === 0 ||
      completedAttempts.some((a) => a.analysisItems?.some((i) => i.promptType === promptType));

    const headers: HeaderConfig[] = [{ name: this.translocoService.translate('COLUMN_DATE', {}, scope), fieldKey: 'date', sortable: false }, { name: this.translocoService.translate('COLUMN_OVERALL_SCORE', {}, scope), fieldKey: 'overallScore', sortable: false }];

    if (hasScoreType('MESSAGE_ADHERENCE') || hasScoreType('MESSAGE_ADHERENCE_V2')) headers.push({ name: this.translocoService.translate('COLUMN_MESSAGING_SCORE', {}, scope), fieldKey: 'messagingScore', sortable: false });
    if (hasScoreType('SCORE_CUSTOMIZED')) headers.push({ name: this.translocoService.translate('COLUMN_METHODOLOGY_SCORE', {}, scope), fieldKey: 'methodologyScore', sortable: false });
    if (hasScoreType('SCORE_SKILLS')) headers.push({ name: this.translocoService.translate('COLUMN_SKILLS_SCORE', {}, scope), fieldKey: 'skillsScore', sortable: false });

    headers.push({ name: this.translocoService.translate('COLUMN_RESULTS', {}, scope), fieldKey: 'results', sortable: false, clickable: true });
    return headers;
  }

  getRolePlayStepPageSize(step: PathwayStep): number {
    return step.practiceCallAttempts?.length ?? 0;
  }

  getRolePlayStepShowAttemptsBadge(step: PathwayStep): boolean {
    if (step.stepType === PathwayStepType.CERTIFICATION_CALL) return step.stepState !== PathwayStepState.COMPLETED;
    return step.attemptsRemaining === 0;
  }

  private hasPriorAttempts(step: PathwayStep): boolean {
    return (step.practiceCallAttempts?.length ?? 0) > 0;
  }

  getRolePlayStepActionHeader(step: PathwayStep): CtaConfig | undefined {
    if (!this.shouldShowHeaderCta(step)) return undefined;
    const isActiveState = [PathwayStepState.COMPLETED, PathwayStepState.IN_PROGRESS, PathwayStepState.OUT_OF_ATTEMPTS].includes(step.stepState);
    if (!isActiveState) return undefined;

    const hasPriorAttempts = this.hasPriorAttempts(step);
    const isCertification = step.stepType === PathwayStepType.CERTIFICATION_CALL;
    const reattemptLabel = this.translocoService.translate(
      isCertification ? 'REATTEMPT_CERTIFICATION' : 'PRACTICE_AGAIN',
      {},
      'learningPathway',
    );

    if ((step.stepState === PathwayStepState.OUT_OF_ATTEMPTS || step.attemptsRemaining === 0) && !this.shouldRequireViewResults(step)) {
      return undefined;
    }

    if (this.shouldRequireViewResults(step)) {
      return this.createPrimaryButtonCta(this.translocoService.translate('VIEW_RESULTS', {}, 'learningPathway'));
    }

    if (!step.hasDebrief) {
      if (!hasPriorAttempts) {
        if (step.optional && step.stepState === PathwayStepState.COMPLETED) {
          return { label: this.translocoService.translate('BEGIN', {}, 'learningPathway'), variant: 'link' };
        }
        return this.createPrimaryButtonCta(this.translocoService.translate('BEGIN', {}, 'learningPathway'));
      }
      return step.stepState === PathwayStepState.COMPLETED
        ? { label: reattemptLabel, variant: 'link' }
        : this.createPrimaryButtonCta(reattemptLabel);
    }
    if (!step.needsReview) {
      if (hasPriorAttempts) {
        return { label: reattemptLabel, variant: 'link' };
      }
      if (step.optional && step.stepState === PathwayStepState.COMPLETED) {
        return { label: this.translocoService.translate('BEGIN', {}, 'learningPathway'), variant: 'link' };
      }
      return this.createPrimaryButtonCta(this.translocoService.translate('BEGIN', {}, 'learningPathway'));
    }
    return step.isLatestDebriefCompleted ? this.createPrimaryButtonCta(reattemptLabel) : { label: reattemptLabel, variant: 'link' };
  }

  getRolePlayStepToggleLabels(stepType: PathwayStepType): { expanded?: string; collapsed?: string } {
    const scope = 'learningPathway';
    if (stepType === PathwayStepType.CERTIFICATION_CALL) {
      return {
        expanded: this.translocoService.translate('COLLAPSE_ATTEMPTS', {}, scope),
        collapsed: this.translocoService.translate('VIEW_ALL_ATTEMPTS', {}, scope),
      };
    }
    return {
      expanded: this.translocoService.translate('COLLAPSE_SESSIONS', {}, scope),
      collapsed: this.translocoService.translate('VIEW_ALL_SESSIONS', {}, scope),
    };
  }

  shouldShowHeaderCta(step: PathwayStep): boolean {
    const latest = this.getMostRecentAttempt(step.practiceCallAttempts ?? []);
    return !latest || (!this.isAttemptProcessing(latest) && !this.pendingEnrollmentRefreshUuids.has(latest.practiceCallUuid));
  }

  shouldRequireViewResults(step: PathwayStep): boolean {
    const latestAttempt = this.getMostRecentAttempt(step.practiceCallAttempts ?? []);
    return !!latestAttempt
      && !this.isAttemptProcessing(latestAttempt)
      && !this.pendingEnrollmentRefreshUuids.has(latestAttempt.practiceCallUuid)
      && this.resultToBeViewedUuids.has(latestAttempt.practiceCallUuid);
  }

  markResultsViewed(practiceCallUuid: string): void {
    if (!practiceCallUuid) return;
    this.resultToBeViewedUuids.delete(practiceCallUuid);
    this.persistResultToBeViewedToStorage();
  }

  shouldSuppressRowViewFeedback(practiceCallUuid: string, steps: PathwayStep[]): boolean {
    if (!practiceCallUuid) return false;
    return steps.some((step) =>
      isRoleplayStepType(step.stepType)
      && this.shouldRequireViewResults(step)
      && this.getMostRecentAttempt(step.practiceCallAttempts ?? [])?.practiceCallUuid === practiceCallUuid
    );
  }

  getRolePlayStepFooterSection(step: PathwayStep): FooterSectionConfig | undefined {
    if (!step.needsReview || !step.hasDebrief || step.isLatestDebriefCompleted == null) return undefined;
    const latest = this.getMostRecentAttempt(step.practiceCallAttempts ?? []);
    if (!latest || !this.attemptHasResults(latest) || this.isAttemptProcessing(latest) || this.pendingEnrollmentRefreshUuids.has(latest.practiceCallUuid)) return undefined;
    const scope = 'learningPathway';
    const title = this.translocoService.translate('DEBRIEF_TITLE', {}, scope);
    const descriptionKey = step.stepType === PathwayStepType.PRACTICE_CALL ? 'DEBRIEF_DESCRIPTION_PRACTICE' : 'DEBRIEF_DESCRIPTION';
    const description = this.translocoService.translate(descriptionKey, {}, scope);
    const cta = step.isLatestDebriefCompleted
      ? { label: this.translocoService.translate('DEBRIEF_LATEST_SESSION', {}, scope), variant: 'link' as const }
      : this.createPrimaryButtonCta(this.translocoService.translate('BEGIN', {}, scope));
    return { title, description, cta };
  }

  private createPrimaryButtonCta(label: string): CtaConfig {
    return { label, variant: 'button', buttonKind: 'primary', buttonSize: 'xsmall' };
  }

  private getLatestCallAttempts(steps: PathwayStep[]): IPracticeCallAttemptResult[] {
    return steps.reduce<IPracticeCallAttemptResult[]>((acc, step) => {
      if (!isRoleplayStepType(step.stepType)) return acc;
      const latest = this.getMostRecentAttempt(step.practiceCallAttempts ?? []);
      if (latest) acc.push(latest);
      return acc;
    }, []);
  }

  private startPollingIfProcessing(steps: PathwayStep[]): void {
    if (this.currentPollingUuid) return;
    const attempt = this.getLatestCallAttempts(steps).find((a) => this.isAttemptProcessing(a));
    if (attempt) {
      this.currentPollingUuid = attempt.practiceCallUuid;
      this.resultsService.shouldFetchPromptsData.set(false);
      this.resultsService.init(attempt.practiceCallUuid);
    }
  }

  private restoreUuidsFromStorage(storageKey: string, targetSet: Set<string>, steps: PathwayStep[]): void {
    try {
      const stored = localStorage.getItem(storageKey);
      if (!stored) return;
      const storedUuids = new Set<string>(JSON.parse(stored));
      const validUuids = this.getLatestCallAttempts(steps)
        .map((a) => a.practiceCallUuid)
        .filter((uuid) => storedUuids.has(uuid));
      validUuids.forEach((uuid) => targetSet.add(uuid));
      if (validUuids.length !== storedUuids.size) {
        localStorage.setItem(storageKey, JSON.stringify(validUuids));
      }
    } catch {}
  }

  private persistResultToBeViewedToStorage(): void {
    try {
      localStorage.setItem(this.resultToBeViewedStorageKey, JSON.stringify([...this.resultToBeViewedUuids]));
    } catch {}
  }

  private shouldUseHeaderViewResultsForAttempt(
    attempt: IPracticeCallAttemptResult,
    isMostRecent: boolean,
  ): boolean {
    return isMostRecent
      && !this.isAttemptProcessing(attempt)
      && this.resultToBeViewedUuids.has(attempt.practiceCallUuid);
  }

  ngOnDestroy(): void {
    this.stopPolling();
    this.stopQuizPolling();
    PathwayLocalStorageUtil.clearRoleplayPending();
    this.pendingEnrollmentRefreshUuids.clear();
    this.shouldRefreshEnrollment$.complete();
  }
}
